/**
 * Match Sport API helpers. 50-level campaign + a daily challenge: start today's session, then
 * report each level end. The server owns the economy — coins depend only on the level identity +
 * per-day paid set + daily cap, never on a client-supplied amount. `stars`/`score` are advisory
 * (analytics) only. Level 0 is the daily challenge (matching the runtime's own convention).
 */

import type { useApiClient } from '../../../helpers/apiClient.helper';
import { API_ENDPOINTS } from '../../../constants/api';
import { MATCH_SPORT_GAME_KEY } from '../constants/matchSport.constants';

type ApiClient = ReturnType<typeof useApiClient>;

export interface MatchSportStart {
  levelCount: number;
  coinsLeftToday: number;
  capReached: boolean;
  /** Level keys ("0"–"50") already paid today. */
  paidLevels: string[];
}

export interface MatchSportClearResult {
  coinsAwarded: number;
  /** New server coin balance; omitted for anonymous users (never banked). */
  coins?: number;
  coinsLeftToday: number;
  capReached: boolean;
}

export async function startMatchSportRun(apiClient: ApiClient): Promise<MatchSportStart> {
  const response = await apiClient<{ success: boolean; data: MatchSportStart; message?: string }>(
    API_ENDPOINTS.games.matchStart(MATCH_SPORT_GAME_KEY),
    { method: 'POST', data: {} },
  );
  const payload = response.data;
  if (!payload.success) throw new Error(payload.message ?? 'Could not start the game');
  return payload.data;
}

export async function recordMatchSportLevelClear(
  apiClient: ApiClient,
  args: { level: number; stars: number; score: number },
): Promise<MatchSportClearResult> {
  const response = await apiClient<{ success: boolean; data: MatchSportClearResult; message?: string }>(
    API_ENDPOINTS.games.matchClear(MATCH_SPORT_GAME_KEY),
    { method: 'POST', data: args },
  );
  const payload = response.data;
  if (!payload.success) throw new Error(payload.message ?? 'Could not record the level');
  return payload.data;
}
